import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Dropdown,
  Header,
  Segment,
  Grid,
  Message,
  Container,
  Icon,
  Statistic,
} from "semantic-ui-react";
import axios from "axios";
import { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";
import Constants from "../util/Constants";
import Utils from "../util/Utils";
import SymbolSearch from "../components/SymbolSearch";

const WhatIfCalculator = () => {
  const [symbol, setSymbol] = useState("");
  const [investmentAmount, setInvestmentAmount] = useState("1000");
  const [selectedDate, setSelectedDate] = useState("");
  const [currentValue, setCurrentValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historicalPrice, setHistoricalPrice] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [ipoDate, setIpoDate] = useState(null);

  const dateOptions = [
    { key: "1d", text: "1 day ago", value: "1d" },
    { key: "1w", text: "1 week ago", value: "1w" },
    { key: "1m", text: "1 month ago", value: "1m" },
    { key: "3m", text: "3 months ago", value: "3m" },
    { key: "6m", text: "6 months ago", value: "6m" },
    { key: "1y", text: "1 year ago", value: "1y" },
    { key: "5y", text: "5 years ago", value: "5y" },
    { key: "10y", text: "10 years ago", value: "10y" },
    { key: "20y", text: "20 years ago", value: "20y" },
    { key: "ipo", text: "Since IPO", value: "ipo" },
  ];

  const amountOptions = [
    { key: "100", text: "$100", value: "100" },
    { key: "1000", text: "$1,000", value: "1000" },
    { key: "10000", text: "$10,000", value: "10000" },
    { key: "100000", text: "$100,000", value: "100000" },
  ];

  const getDateFromOption = (option) => {
    const today = new Date();
    switch (option) {
      case "1d":
        return new Date(today.setDate(today.getDate() - 1));
      case "1w":
        return new Date(today.setDate(today.getDate() - 7));
      case "1m":
        return new Date(today.setMonth(today.getMonth() - 1));
      case "3m":
        return new Date(today.setMonth(today.getMonth() - 3));
      case "6m":
        return new Date(today.setMonth(today.getMonth() - 6));
      case "1y":
        return new Date(today.setFullYear(today.getFullYear() - 1));
      case "5y":
        return new Date(today.setFullYear(today.getFullYear() - 5));
      case "10y":
        return new Date(today.setFullYear(today.getFullYear() - 10));
      case "20y":
        return new Date(today.setFullYear(today.getFullYear() - 20));
      case "ipo":
        return ipoDate;
      default:
        return today;
    }
  };

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const fetchIpoDate = async (symbol) => {
    try {
      var url = `${FMP_API_ENDPOINTS.PROFILE(symbol)}?${API_KEYS.FMP_API_KEY}`;
      const response = await axios.get(url);

      if (
        !response.data ||
        !Array.isArray(response.data) ||
        response.data.length === 0
      ) {
        console.error("No IPO data found in response");
        throw new Error("No IPO data available");
      }

      const companyData = response.data[0];

      if (!companyData.ipoDate) {
        console.error("No IPO date in company data");
        throw new Error("No IPO date available for this company");
      }

      const ipoDate = new Date(companyData.ipoDate);
      setIpoDate(ipoDate);
      return ipoDate;
    } catch (err) {
      console.error("Error fetching IPO date:", err);
      throw new Error("Could not fetch IPO date");
    }
  };

  const findNearestTradingDay = async (symbol, targetDate) => {
    try {
      const startDate = new Date(targetDate);
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date(targetDate);
      endDate.setDate(endDate.getDate() + 7);

      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);

      if (!formattedStartDate || !formattedEndDate) {
        throw new Error("Invalid date range");
      }

      const url = `${FMP_API_ENDPOINTS.PRICE_HISTORY(symbol)}?${
        API_KEYS.FMP_API_KEY
      }&from=${formattedStartDate}&to=${formattedEndDate}`;

      const response = await axios.get(url);

      if (
        !response.data ||
        !response.data.historical ||
        response.data.historical.length === 0
      ) {
        throw new Error("No historical data available in the date range");
      }

      const historicalData = response.data.historical
        .filter((item) => item && item.date)
        .sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

      if (historicalData.length === 0) {
        throw new Error("No valid historical data available");
      }

      const targetTimestamp = new Date(targetDate).getTime();
      let closestDate = historicalData[0];
      
      if (!closestDate || !closestDate.date) {
        throw new Error("First historical data item is invalid");
      }
      
      let minDiff = Math.abs(
        new Date(closestDate.date).getTime() - targetTimestamp
      );

      historicalData.forEach((data) => {
        if (data && data.date) {
          const currentDiff = Math.abs(
            new Date(data.date).getTime() - targetTimestamp
          );
          if (currentDiff < minDiff) {
            minDiff = currentDiff;
            closestDate = data;
          }
        }
      });

      if (!closestDate || !closestDate.date) {
        throw new Error("Could not find valid closest date");
      }

      return {
        date: new Date(closestDate.date),
        price: closestDate.close,
      };
    } catch (err) {
      console.error("Error finding nearest trading day:", err);
      throw new Error("Could not find nearest trading day");
    }
  };

  const fetchHistoricalPrice = async (symbol, date) => {
    try {
      const formattedDate = formatDate(date);
      if (!formattedDate) {
        throw new Error("Invalid date format");
      }

      const url = `${FMP_API_ENDPOINTS.PRICE_HISTORY(symbol)}?${
        API_KEYS.FMP_API_KEY
      }&from=${formattedDate}&to=${formattedDate}`;

      const response = await axios.get(url);

      if (
        !response.data ||
        !response.data.historical ||
        response.data.historical.length === 0
      ) {
        const nearestDay = await findNearestTradingDay(symbol, date);
        return nearestDay.price;
      }

      const historicalData = response.data.historical[0];

      if (!historicalData || !historicalData.close) {
        console.error("No close price in historical data");
        throw new Error("No close price available in historical data");
      }

      return historicalData.close;
    } catch (err) {
      console.error("Error fetching historical price:", err);
      throw new Error("Could not fetch historical price");
    }
  };

  const fetchCurrentPrice = async (symbol) => {
    try {
      var url = `${FMP_API_ENDPOINTS.QUOTE(symbol)}?${API_KEYS.FMP_API_KEY}`;
      const response = await axios.get(url);

      if (!response.data || response.data.length === 0) {
        console.error("No current price data found");
        throw new Error("No current price data available");
      }

      const currentData = response.data[0];
      if (!currentData.price) {
        console.error("No price in current data");
        throw new Error("No price available in current data");
      }

      return currentData.price;
    } catch (err) {
      console.error("Error fetching current price:", err);
      throw new Error("Could not fetch current price");
    }
  };

  const calculateInvestment = async () => {
    if (!symbol || !selectedDate || !investmentAmount) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let investmentDate;

      // If IPO date is needed, fetch it first
      if (selectedDate === "ipo") {
        const ipoDateResponse = await fetchIpoDate(symbol);
        if (!ipoDateResponse) {
          throw new Error("Could not determine IPO date");
        }
        investmentDate = ipoDateResponse;
      } else {
        investmentDate = getDateFromOption(selectedDate);
      }

      const [historicalPrice, currentPrice] = await Promise.all([
        fetchHistoricalPrice(symbol, investmentDate),
        fetchCurrentPrice(symbol),
      ]);

      setHistoricalPrice(historicalPrice);
      setCurrentPrice(currentPrice);

      // Calculate current value
      const shares = parseFloat(investmentAmount) / historicalPrice;
      const currentValue = shares * currentPrice;

      setCurrentValue(currentValue);
    } catch (err) {
      console.error("Error in calculateInvestment:", err);
      setError(err.message || "Error calculating investment value");
    } finally {
      setLoading(false);
    }
  };

  const resetCalculator = () => {
    setSymbol("");
    setInvestmentAmount("1000");
    setSelectedDate("");
    setCurrentValue(null);
    setHistoricalPrice(null);
    setCurrentPrice(null);
    setIpoDate(null);
    setError(null);
  };

  useEffect(() => {
    const fetchSymbolsList = async () => {
      try {
        await axios.get(Constants.INITIALIZE_URL);
      } catch (error) {
        console.error("Error fetching symbols list:", error);
      }
    };
    fetchSymbolsList();
  }, []);

  return (
    <Container style={{ padding: "0.25rem", marginBottom: "1rem" }}>
      <Card
        fluid
        style={{
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          border: "none",
          marginBottom: "1rem",
        }}
      >
        <Card.Content style={{ padding: "0.75rem" }}>
          <Header
            as="h2"
            textAlign="center"
            style={{
              marginBottom: "0.75rem",
              color: "#2c3e50",
              fontSize: "1.5rem",
            }}
          >
            <Icon name="calculator" />
            What If Investment Calculator
          </Header>

          <Form size="large">
            <Grid stackable>
              <Grid.Row>
                <Grid.Column width={16}>
                  <Form.Field>
                    <label
                      style={{
                        fontSize: "1rem",
                        color: "#34495e",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Stock Symbol
                    </label>
                    <SymbolSearch
                      onSymbolSelect={setSymbol}
                      initialValue={symbol}
                      fluid
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row columns={2} style={{ marginTop: "0.5rem" }}>
                <Grid.Column>
                  <Form.Field>
                    <label
                      style={{
                        fontSize: "1rem",
                        color: "#34495e",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Investment Date
                    </label>
                    <Dropdown
                      placeholder="Select investment date"
                      fluid
                      selection
                      options={dateOptions}
                      value={selectedDate}
                      onChange={(e, { value }) => setSelectedDate(value)}
                      size="large"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label
                      style={{
                        fontSize: "1rem",
                        color: "#34495e",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Investment Amount
                    </label>
                    <Dropdown
                      placeholder="Select amount"
                      fluid
                      selection
                      options={amountOptions}
                      value={investmentAmount}
                      onChange={(e, { value }) => setInvestmentAmount(value)}
                      size="large"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <Button.Group
                    fluid
                    size="large"
                    style={{ marginTop: "0.75rem" }}
                  >
                    <Button
                      primary
                      onClick={calculateInvestment}
                      loading={loading}
                      style={{
                        borderRadius: "8px 0 0 8px",
                        backgroundColor: "#3498db",
                        "&:hover": {
                          backgroundColor: "#2980b9",
                        },
                      }}
                    >
                      <Icon name="calculator" />
                      Calculate
                    </Button>
                    <Button
                      secondary
                      onClick={resetCalculator}
                      style={{
                        borderRadius: "0 8px 8px 0",
                        backgroundColor: "#95a5a6",
                        "&:hover": {
                          backgroundColor: "#7f8c8d",
                        },
                      }}
                    >
                      <Icon name="refresh" />
                      Reset
                    </Button>
                  </Button.Group>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>

          {error && (
            <Message
              negative
              style={{
                marginTop: "1rem",
                borderRadius: "8px",
                backgroundColor: "#fdf3f2",
                border: "1px solid #f5c6cb",
              }}
            >
              <Message.Header>
                <Icon name="warning circle" />
                Error
              </Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {currentValue && (
            <Segment
              style={{
                marginTop: "1.5rem",
                padding: "1rem",
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                border: "none",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                marginBottom: "0.5rem",
              }}
            >
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={16} textAlign="center">
                    <Header
                      as="h4"
                      style={{
                        color: "#2c3e50",
                        marginBottom: "0.25rem",
                        fontSize: "1.2rem",
                      }}
                    >
                      Investment Worth
                    </Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Grid
                      columns={5}
                      stackable
                      centered
                      style={{ margin: "0" }}
                    >
                      <Grid.Column
                        textAlign="center"
                        style={{ padding: "0.5rem" }}
                      >
                        <Statistic
                          label="Initial Investment"
                          value={Utils.format_currency_usd(
                            parseFloat(investmentAmount)
                          )}
                          size="tiny"
                          style={{
                            ".statistic .value": {
                              fontSize: "1.1rem !important",
                            },
                            ".statistic .label": {
                              fontSize: "0.8rem !important",
                            },
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column
                        textAlign="center"
                        style={{ padding: "0.5rem" }}
                      >
                        <Statistic
                          label="Historical Price"
                          value={Utils.format_currency_usd(historicalPrice)}
                          size="tiny"
                          style={{
                            ".statistic .value": {
                              fontSize: "1.1rem !important",
                            },
                            ".statistic .label": {
                              fontSize: "0.8rem !important",
                            },
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column
                        textAlign="center"
                        style={{ padding: "0.5rem" }}
                      >
                        <Statistic
                          label="Current Price"
                          value={Utils.format_currency_usd(currentPrice)}
                          size="tiny"
                          style={{
                            ".statistic .value": {
                              fontSize: "1.1rem !important",
                            },
                            ".statistic .label": {
                              fontSize: "0.8rem !important",
                            },
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column
                        textAlign="center"
                        style={{ padding: "0.5rem" }}
                      >
                        <Statistic
                          label="Current Value"
                          value={Utils.format_currency_usd(currentValue)}
                          color={
                            currentValue > parseFloat(investmentAmount)
                              ? "green"
                              : "red"
                          }
                          size="tiny"
                          style={{
                            ".statistic .value": {
                              fontSize: "1.1rem !important",
                            },
                            ".statistic .label": {
                              fontSize: "0.8rem !important",
                            },
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column
                        textAlign="center"
                        style={{ padding: "0.5rem" }}
                      >
                        <Statistic
                          label="Return"
                          value={Utils.format_percent(
                            (currentValue - parseFloat(investmentAmount)) /
                              parseFloat(investmentAmount)
                          )}
                          color={
                            currentValue > parseFloat(investmentAmount)
                              ? "green"
                              : "red"
                          }
                          size="tiny"
                          style={{
                            ".statistic .value": {
                              fontSize: "1.1rem !important",
                            },
                            ".statistic .label": {
                              fontSize: "0.8rem !important",
                            },
                          }}
                        />
                      </Grid.Column>
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          )}
        </Card.Content>
      </Card>
    </Container>
  );
};

export default WhatIfCalculator;
